import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import { NO_DRAG_CLASS } from './constants';
import {
  PlaceholderSuggestionList,
  usePlaceholderSuggestions,
} from './PlaceholderSuggestions';
import type {
  BraceMetricPicker,
  StudioTextHandle,
  TextVerticalAlign,
} from './studioTextTypes';
import { SubtextPlaceholder, insertPlaceholder } from './subtextPlaceholders';
import { PickerAnchor, TypingHint } from './textPickerAnchor';

const LINE_HEIGHT = 20;
// Below the line being typed on
const SUGGESTIONS_OFFSET = 4;

const JUSTIFY: Record<TextVerticalAlign, string> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
};

const Wrapper = styled.div<{ $verticalAlign: TextVerticalAlign }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => JUSTIFY[props.$verticalAlign]};
  min-width: 0;
  min-height: 0;
`;

// A KPI tile's scale: # for a headline number, ## and ### for its label,
// #### to ###### and paragraphs for captions. Colours follow the tile's
// (it points --color-text-primary at its own, see textBackground), so the
// muted levels fade the text colour rather than pick another.
const Rendered = styled.div`
  min-height: 0;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: 14px;
  line-height: 1.4;
  overflow-wrap: break-word;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: inherit;
    margin: 0 0 4px;
  }

  h1 {
    font-size: 48px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
  }

  h5 {
    font-size: 13px;
    font-weight: 500;
    opacity: 0.75;
  }

  h6 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.65;
  }

  p,
  ul,
  ol,
  blockquote {
    margin: 0 0 8px;
  }

  ul,
  ol {
    padding-left: 20px;
  }

  blockquote {
    padding-left: 10px;
    border-left: 3px solid currentColor;
    opacity: 0.75;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  code {
    font-size: 0.9em;
    padding: 1px 4px;
    border-radius: 4px;
    background-color: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
  }

  hr {
    border: none;
    border-top: 1px solid currentColor;
    opacity: 0.2;
    margin: 8px 0;
  }

  table {
    border-collapse: collapse;
  }

  td,
  th {
    padding: 4px 8px;
    text-align: left;
    border-bottom: 1px solid var(--color-border-secondary);
  }
`;

// Fills the block, or when centred or at its foot is only as tall as its
// text (see useLayoutEffect), so the block can place it
const Source = styled.textarea<{ $isFitted: boolean }>`
  flex: ${(props) => (props.$isFitted ? '0 0 auto' : '1')};
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  width: 100%;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: var(--color-text-primary);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  line-height: ${LINE_HEIGHT}px;
`;

// Measures the source's (monospace) character width, to place the metric
// picker at a '{' typed
const CharMeasure = styled.span`
  position: absolute;
  visibility: hidden;
  white-space: pre;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
`;
const MEASURED_CHARS = '0000000000';

const Empty = styled.div`
  color: var(--color-text-tertiary);
`;

const NO_PLACEHOLDERS: SubtextPlaceholder[] = [];

// A Text block's Markdown. Rendered (raw HTML left out) until its block is
// selected, then typed as source in place, aligned as it's shown. Typing '{'
// opens the library's metric picker at it, as rich text does (or, without
// one, suggests the block's placeholders), and its card can type one in (see
// editorRef). While editing it's excluded from dragging, so selecting text
// doesn't move the block.
const MarkdownText = ({
  id,
  text,
  displayText,
  placeholders = NO_PLACEHOLDERS,
  isEditing,
  verticalAlign = 'top',
  editorRef,
  metricPicker,
  onChange,
}: {
  id: string;
  text: string | undefined;
  // Shown instead of the text while not editing (its placeholders filled
  // in)
  displayText?: string;
  // Suggested while a {placeholder} is typed
  placeholders?: SubtextPlaceholder[];
  isEditing: boolean;
  verticalAlign?: TextVerticalAlign;
  // For its card's Add metric (see TextCard)
  editorRef?: React.MutableRefObject<StudioTextHandle | null>;
  // A Text block's library picker, opened at a '{' typed
  metricPicker?: BraceMetricPicker;
  onChange: (text: string) => void;
}) => {
  const sourceRef = useRef<HTMLTextAreaElement | null>(null);
  const charMeasureRef = useRef<HTMLSpanElement | null>(null);
  // The '{' that opened the metric picker, which what's picked replaces
  const pendingStartRef = useRef<number | undefined>(undefined);
  // Where that '{' is in the block: its line's top, and its left and the
  // caret's after it
  const [pickerAt, setPickerAt] = useState<{
    top: number;
    left: number;
    caretLeft: number;
  }>({ top: 0, left: 0, caretLeft: 0 });
  const isFitted = verticalAlign !== 'top';
  const [suggestionsAt, setSuggestionsAt] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  // Replaces start to the caret with {key}, the caret after it
  const typeIn = useCallback(
    ({ key, start }: { key: string; start: number }) => {
      const source = sourceRef.current;
      if (!source) {
        return;
      }
      const next = insertPlaceholder({
        text: source.value,
        start,
        caret: source.selectionEnd,
        key,
      });
      onChange(next.text);
      // After React has put the new text in
      requestAnimationFrame(() => {
        source.focus();
        source.setSelectionRange(next.caret, next.caret);
      });
    },
    [onChange],
  );
  const onChoose = useCallback(
    ({
      placeholder,
      start,
    }: {
      placeholder: SubtextPlaceholder;
      start: number;
    }) => typeIn({ key: placeholder.key, start }),
    [typeIn],
  );

  // What's picked goes in place of the '{' that opened the picker, or from
  // the card's Add metric at the caret (where it last was), over what's
  // selected
  useImperativeHandle(
    editorRef,
    () => ({
      getEditor: () => undefined,
      insertPlaceholder: (key: string) => {
        const source = sourceRef.current;
        const start = pendingStartRef.current;
        pendingStartRef.current = undefined;
        if (source) {
          typeIn({ key, start: start ?? source.selectionStart });
        }
      },
      // Back to typing where it was, e.g. after the '{' once the picker
      // closes without a pick
      focus: () => {
        pendingStartRef.current = undefined;
        sourceRef.current?.focus();
      },
    }),
    [typeIn],
  );
  // Where the picker opens instead, its metrics aren't suggested too
  const suggestions = usePlaceholderSuggestions({
    placeholders: metricPicker ? NO_PLACEHOLDERS : placeholders,
    onChoose,
  });

  useEffect(() => {
    if (!isEditing) {
      pendingStartRef.current = undefined;
    }
  }, [isEditing]);

  // A '{' typed opens the metric picker at it
  const openPickerAt = useCallback(
    (index: number) => {
      const source = sourceRef.current;
      if (!source || !metricPicker) {
        return;
      }
      const lines = source.value.slice(0, index).split('\n');
      const charWidth = charMeasureRef.current
        ? charMeasureRef.current.getBoundingClientRect().width /
          MEASURED_CHARS.length
        : 0;
      const left =
        source.offsetLeft + lines[lines.length - 1].length * charWidth;
      pendingStartRef.current = index;
      setPickerAt({
        top:
          source.offsetTop +
          (lines.length - 1) * LINE_HEIGHT -
          source.scrollTop,
        left,
        caretLeft: left + charWidth,
      });
      metricPicker.onOpen();
    },
    [metricPicker],
  );

  // Centred or at the foot, the source is as tall as its text (up to the
  // block's height)
  useLayoutEffect(() => {
    const source = sourceRef.current;
    if (!source) {
      return;
    }
    source.style.height = '';
    if (isFitted && source.scrollHeight > 0) {
      source.style.height = `${source.scrollHeight}px`;
    }
  }, [isFitted, isEditing, text]);
  const { update: updateSuggestions, close: closeSuggestions } = suggestions;

  // From the text before the caret, under the line it's on
  const syncSuggestions = useCallback(() => {
    const source = sourceRef.current;
    if (!source || placeholders.length === 0 || metricPicker) {
      closeSuggestions();
      return;
    }
    const caret = source.selectionStart;
    updateSuggestions({ text: source.value, caret });
    const line = source.value.slice(0, caret).split('\n').length;
    setSuggestionsAt({
      top:
        source.offsetTop +
        line * LINE_HEIGHT -
        source.scrollTop +
        SUGGESTIONS_OFFSET,
      left: 0,
    });
  }, [closeSuggestions, metricPicker, placeholders.length, updateSuggestions]);

  if (isEditing) {
    return (
      <Wrapper
        $verticalAlign={verticalAlign}
        className={NO_DRAG_CLASS}
        data-testid={`studio-markdown-${id}`}
        data-editing
        data-vertical-align={verticalAlign}
        onKeyDownCapture={(e) => {
          suggestions.onKeyDown(e);
        }}
        // Fitted, the source doesn't fill the block: a click around it
        // still types into it
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && sourceRef.current) {
            e.preventDefault();
            sourceRef.current.focus();
          }
        }}
      >
        <Source
          ref={sourceRef}
          $isFitted={isFitted}
          value={text ?? ''}
          placeholder={
            metricPicker
              ? '# 42\n### Headline\nType Markdown, or { to add a metric'
              : '# 42\n### Headline\nType Markdown…'
          }
          spellCheck={false}
          onChange={(e) => {
            const before = text ?? '';
            const after = e.target.value;
            const caret = e.target.selectionStart;
            onChange(after);
            // A single '{' typed, before the caret
            const isBraceTyped =
              after.length === before.length + 1 &&
              after[caret - 1] === '{' &&
              after.slice(0, caret - 1) + after.slice(caret) === before;
            if (isBraceTyped && metricPicker) {
              openPickerAt(caret - 1);
            } else {
              syncSuggestions();
            }
          }}
          onSelect={syncSuggestions}
          onBlur={closeSuggestions}
          data-testid={`studio-markdown-source-${id}`}
        />
        <CharMeasure ref={charMeasureRef} aria-hidden>
          {MEASURED_CHARS}
        </CharMeasure>
        {metricPicker && metricPicker.isOpen && (
          <TypingHint
            style={{
              top: pickerAt.top,
              left: pickerAt.caretLeft,
              height: LINE_HEIGHT,
            }}
            data-testid={`studio-text-typing-hint-${id}`}
          >
            Select a metric
          </TypingHint>
        )}
        {metricPicker && (
          <PickerAnchor style={{ top: pickerAt.top, left: pickerAt.left }}>
            {metricPicker.render(<div style={{ height: LINE_HEIGHT }} />)}
          </PickerAnchor>
        )}
        {suggestions.isOpen && (
          <PlaceholderSuggestionList
            matches={suggestions.matches}
            activeIndex={suggestions.activeIndex}
            position={suggestionsAt}
            onHover={suggestions.setActiveIndex}
            onChoose={suggestions.choose}
          />
        )}
      </Wrapper>
    );
  }

  const shown = displayText ?? text ?? '';
  return (
    <Wrapper
      $verticalAlign={verticalAlign}
      data-testid={`studio-markdown-${id}`}
      data-vertical-align={verticalAlign}
    >
      {shown.trim() === '' ? (
        <Empty>Type something…</Empty>
      ) : (
        <Rendered>
          <Markdown remarkPlugins={[remarkGfm]} skipHtml>
            {shown}
          </Markdown>
        </Rendered>
      )}
    </Wrapper>
  );
};

export default MarkdownText;
